import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, TrendingDown, ShieldCheck, Leaf, Users, ArrowLeft } from "lucide-react";

export default function CaseStudies() {
  const cases = [
    {
      id: 1,
      title: "TechGiant Campus Optimization",
      industry: "Technology",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c",
      challenge: "A leading tech park with 5,000+ employees faced severe traffic congestion at entry gates and low seat utilization (60%) in their fixed-route shuttles.",
      solution: "Implemented WorkRidePro's AI-driven dynamic routing. We replaced fixed routes with 'smart stops' and introduced an app-based seat booking system.",
      results: [
        { label: "Cost Reduction", value: "10%", icon: TrendingDown },
        { label: "Seat Utilization", value: "92%", icon: Users },
        { label: "Gate Congestion", value: "-45%", icon: ArrowUpRight }
      ]
    },
    {
      id: 2,
      title: "SafeShift for 24/7 BPO",
      industry: "BPO / ITeS",
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7",
      challenge: "Ensuring absolute safety for 1,200 female employees during late-night shifts (10 PM - 6 AM) while managing complex, changing rosters.",
      solution: "Deployed our 'SafeGuard' protocol: GPS-enabled cabs with panic buttons, live command center monitoring, and automated 'Safe Drop' confirmation calls.",
      results: [
        { label: "Safety Incidents", value: "0", icon: ShieldCheck },
        { label: "Compliance", value: "100%", icon: ShieldCheck },
        { label: "Employee Trust", value: "4.9/5", icon: Users }
      ]
    },
    {
      id: 3,
      title: "GreenCommute Initiative",
      industry: "Consulting",
      image: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7",
      challenge: "A global consulting firm wanted to align their employee transport with their aggressive ESG goals to reach Net Zero by 2030.",
      solution: "Phased transition of their entire fleet of 200 diesel cabs to 100% Electric Vehicles (EVs) within 12 months, supported by charging infrastructure planning.",
      results: [
        { label: "Carbon Emissions", value: "0g", icon: Leaf },
        { label: "Fuel Costs", value: "-40%", icon: TrendingDown },
        { label: "Brand Value", value: "+High", icon: ArrowUpRight }
      ]
    },
    {
      id: 4,
      title: "Multi-Plant Shift Sync",
      industry: "Manufacturing",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
      challenge: "A manufacturing giant struggled to manage shift transport across 3 different plant locations with overlapping timings, leading to vehicle redundancy.",
      solution: "Integrated roster management system that synchronized shifts across plants. Introduced inter-plant shuttle loops to maximize vehicle uptime.",
      results: [
        { label: "Fleet Size", value: "-25%", icon: TrendingDown },
        { label: "On-Time Shifts", value: "99.5%", icon: ArrowUpRight },
        { label: "Admin Hours", value: "-150/mo", icon: Users }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans antialiased dark">
      <Navbar />
      
      <main className="pt-24 pb-20">
        {/* Header */}
        <div className="container mx-auto px-6 mb-16 text-center relative">
          <div className="absolute left-6 top-0 hidden md:block">
            <Button variant="ghost" asChild className="gap-2">
              <Link to="/">
                <ArrowLeft className="h-4 w-4" /> Back to Home
              </Link>
            </Button>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-primary mb-6">
            Proven <span className="text-secondary">Results</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            See how WorkRidePro transforms employee transport for industry leaders across technology, manufacturing, and services.
          </p>
          
          <div className="md:hidden mt-6">
            <Button variant="outline" asChild className="gap-2">
              <Link to="/">
                <ArrowLeft className="h-4 w-4" /> Back to Home
              </Link>
            </Button>
          </div>
        </div>

        {/* Case Studies Grid */}
        <div className="container mx-auto px-6">
          <div className="grid gap-12">
            {cases.map((study, index) => (
              <Card key={study.id} className={`overflow-hidden border-none shadow-2xl bg-card ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex group`}>
                {/* Image Section */}
                <div className="lg:w-2/5 relative h-64 lg:h-auto overflow-hidden">
                  <img 
                    src={study.image} 
                    alt={study.title} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/90 text-primary hover:bg-white text-sm px-3 py-1">
                      {study.industry}
                    </Badge>
                  </div>
                </div>

                {/* Content Section */}
                <div className="lg:w-3/5 p-8 md:p-12 flex flex-col justify-center">
                  <CardHeader className="p-0 mb-6">
                    <CardTitle className="text-3xl font-bold text-primary mb-2">
                      {study.title}
                    </CardTitle>
                    <div className="h-1 w-20 bg-secondary rounded-full"></div>
                  </CardHeader>
                  
                  <CardContent className="p-0 space-y-8">
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-foreground mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span> The Challenge
                        </h4>
                        <p className="text-muted-foreground leading-relaxed">
                          {study.challenge}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span> The Solution
                        </h4>
                        <p className="text-muted-foreground leading-relaxed">
                          {study.solution}
                        </p>
                      </div>
                    </div>

                    {/* Results Metrics */}
                    <div className="bg-muted/30 rounded-2xl p-6 border border-border">
                      <div className="grid grid-cols-3 gap-4">
                        {study.results.map((res, i) => (
                          <div key={i} className="text-center">
                            <div className="flex justify-center mb-2">
                              <res.icon className="h-6 w-6 text-secondary" />
                            </div>
                            <div className="text-2xl md:text-3xl font-bold text-primary mb-1">
                              {res.value}
                            </div>
                            <div className="text-xs md:text-sm text-muted-foreground font-medium uppercase tracking-wide">
                              {res.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
